/**
 * An instant, back into a day (WI-039).
 *
 * `parsePlayedOn` goes the other way. This is its inverse, plus the display
 * form a list needs.
 *
 * Both read LOCAL components rather than UTC ones, which is the whole
 * subtlety here:
 *
 *   - A date somebody typed is stored at midday UTC, deliberately, so that
 *     the calendar day survives being read anywhere between UTC-11 and
 *     UTC+11. Reading local components recovers exactly the day they typed.
 *   - A one-tap play is stored as `now`, an ordinary instant. At 23:30 in
 *     London that is already tomorrow in UTC, so reading UTC components would
 *     show the play on the day after it happened -- and prefill the editor
 *     with a date that silently moves it the moment somebody saved.
 *
 * Local is right for both. UTC is right for neither.
 */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

function parse(iso: string): Date | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * `YYYY-MM-DD`, ready to prefill the edit field.
 *
 * Empty for anything that is not an instant, so a corrupt row leaves a blank
 * field rather than the word "Invalid" for somebody to save.
 */
export function toDateInput(iso: string): string {
  const date = parse(iso);
  if (date === null) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * How a play's date reads in a list.
 *
 * `now` is a parameter rather than a call, so this can be tested at a fixed
 * point without freezing the clock for everything else in the file.
 */
export function formatPlayedAt(iso: string, now: Date = new Date()): string {
  const date = parse(iso);
  if (date === null) return "Unknown date";

  if (isSameDay(date, now)) return "Today";

  // Constructed rather than subtracted, so the month and year roll back with
  // the day. `now - 86400000` is wrong across a DST boundary.
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (isSameDay(date, yesterday)) return "Yesterday";

  const day = `${date.getDate()} ${MONTHS[date.getMonth()]}`;

  // The year, but only when it is not the obvious one. "3 Aug" reads better
  // for a play last week; "3 Aug 2025" is essential for one last year, and
  // the two must not look identical.
  return date.getFullYear() === now.getFullYear() ? day : `${day} ${date.getFullYear()}`;
}
